/**
 * WeaveIn.ai Platform - Case Episodes API Service
 * TypeScript service layer for episode-based case management (ECM, Doula, CHW).
 * Manages case episodes, lifecycle transitions, and new-case detection.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type EpisodeStatus = 'NEW' | 'ACTIVE' | 'SUSPENDED' | 'COMPLETED' | 'CLOSED' | 'ARCHIVED';

export interface CaseEpisode {
  id: number;
  cbo_id: number;
  member_id: number;
  program_id: number;
  affiliation_id?: number;
  case_number: string;
  episode_number: number;
  status: EpisodeStatus;
  is_current: boolean;
  opened_date?: string;
  assigned_date?: string;
  closed_date?: string;
  closed_reason?: string;
  assigned_to_user_id?: number;
  supervisor_user_id?: number;
  health_plan_member_id?: string;
  member_record_status?: string;
  episode_profile_data: Record<string, any>;
  program_specific_data: Record<string, any>;
  enrollment_id?: number;
  created_at?: string;
  updated_at?: string;
  // Joined fields
  assigned_to_name?: string;
  supervisor_name?: string;
  payer_name?: string;
  program_name?: string;
  program_type?: string;
}

export interface EpisodeCreate {
  cbo_id: number;
  member_id: number;
  program_id: number;
  affiliation_id?: number;
  health_plan_member_id?: string;
  member_record_status?: string;
  assigned_to_user_id?: number;
  program_specific_data?: Record<string, any>;
  episode_profile_data?: Record<string, any>;
  service_period_start?: string;
  service_period_end?: string;
  authorized_duration_days?: number;
  authorization_number?: string;
}

export interface EpisodeCreateResponse {
  id: number;
  case_number: string;
  episode_number: number;
  enrollment_id?: number;
  workflow_instance_id?: number;
  workflow_template_name?: string;
}

export interface EpisodeUpdate {
  status?: EpisodeStatus;
  assigned_to_user_id?: number;
  supervisor_user_id?: number;
  health_plan_member_id?: string;
  member_record_status?: string;
  program_specific_data?: Record<string, any>;
  episode_profile_data?: Record<string, any>;
  affiliation_id?: number;
  service_period_start?: string;
  service_period_end?: string;
  authorized_duration_days?: number;
  authorization_number?: string;
}

export interface EpisodeAuditEntry {
  id: number;
  action: string;
  performed_by: number;
  performed_by_name?: string;
  field_changes: Record<string, { old: any; new: any }>;
  before_snapshot?: Record<string, any>;
  after_snapshot?: Record<string, any>;
  performed_at: string;
}

export interface NewCaseDetectRequest {
  member_id: number;
  program_id: number;
  incoming_data: Record<string, any>;
}

export interface NewCaseDetectResult {
  should_create_new: boolean;
  matched_rule_id?: number;
  matched_rule_name?: string;
  action: 'AUTO_CREATE' | 'PROMPT_USER' | 'FLAG_FOR_REVIEW' | 'NONE';
  reason: string;
  existing_episode_id?: number;
  auto_archive_previous: boolean;
}

export interface HouseholdEpisodeCreate {
  cbo_id: number;
  household_id: number;
  program_id: number;
  member_id?: number;
  affiliation_id?: number;
  health_plan_member_id?: string;
  member_record_status?: string;
  assigned_to_user_id?: number;
  program_specific_data?: Record<string, any>;
  episode_profile_data?: Record<string, any>;
}

export interface HouseholdNewCaseDetectRequest {
  household_id: number;
  program_id: number;
  incoming_data: Record<string, any>;
}

export interface CaseHistoryEntry {
  id: number;
  case_number: string;
  episode_number: number;
  status: EpisodeStatus;
  is_current: boolean;
  opened_date?: string;
  closed_date?: string;
  closed_reason?: string;
  assigned_date?: string;
  health_plan_member_id?: string;
  member_record_status?: string;
  program_specific_data?: Record<string, any>;
  household_id?: number;
  program_id: number;
  program_name: string;
  program_type?: string;
  assigned_to_name?: string;
}

export interface CaseHistoryResponse {
  member_id: number;
  episodes: CaseHistoryEntry[];
}

export interface EpisodeDetectionResult {
  action: string;
  should_create_new: boolean;
  reason: string;
  matched_rule_name?: string;
  episode_id?: number;
  case_number?: string;
  existing_episode_id?: number;
  auto_archive_previous: boolean;
}

// ============================================================================
// API CLIENT
// ============================================================================

class CaseEpisodesAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('CaseEpisodes API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          message: error.response?.data?.detail || error.message,
        });
        return Promise.reject(error);
      }
    );
  }

  // ==========================================================================
  // EPISODE CRUD
  // ==========================================================================

  async createEpisode(data: EpisodeCreate): Promise<EpisodeCreateResponse> {
    const res = await this.client.post('/api/case-episodes', data);
    return res.data;
  }

  async listEpisodes(params: {
    member_id: number;
    program_id: number;
    include_archived?: boolean;
  }): Promise<CaseEpisode[]> {
    const res = await this.client.get('/api/case-episodes', { params });
    return res.data;
  }

  async getEpisode(episodeId: number): Promise<CaseEpisode> {
    const res = await this.client.get(`/api/case-episodes/${episodeId}`);
    return res.data;
  }

  async updateEpisode(episodeId: number, data: EpisodeUpdate): Promise<{ status: string }> {
    const res = await this.client.put(`/api/case-episodes/${episodeId}`, data);
    return res.data;
  }

  // ==========================================================================
  // EPISODE LIFECYCLE
  // ==========================================================================

  async closeEpisode(episodeId: number, closedReason: string): Promise<{ status: string }> {
    const res = await this.client.post(`/api/case-episodes/${episodeId}/close`, {
      closed_reason: closedReason,
    });
    return res.data;
  }

  async reopenEpisode(episodeId: number): Promise<{ status: string }> {
    const res = await this.client.post(`/api/case-episodes/${episodeId}/reopen`);
    return res.data;
  }

  // ==========================================================================
  // NEW CASE DETECTION
  // ==========================================================================

  async getEpisodeAuditLog(episodeId: number): Promise<EpisodeAuditEntry[]> {
    const res = await this.client.get(`/api/case-episodes/${episodeId}/audit-log`);
    return res.data;
  }

  async detectNewCase(data: NewCaseDetectRequest): Promise<NewCaseDetectResult> {
    const res = await this.client.post('/api/case-episodes/detect-new-case', data);
    return res.data;
  }

  // ==========================================================================
  // MEMBER CASE HISTORY (cross-program timeline)
  // ==========================================================================

  async getMemberHistory(memberId: number): Promise<CaseHistoryResponse> {
    const res = await this.client.get(`/api/case-episodes/member/${memberId}/history`);
    return res.data;
  }

  // ==========================================================================
  // HOUSEHOLD EPISODES
  // ==========================================================================

  async createHouseholdEpisode(data: HouseholdEpisodeCreate): Promise<CaseEpisode> {
    const res = await this.client.post('/api/case-episodes/household', data);
    return res.data;
  }

  async listHouseholdEpisodes(householdId: number, params?: {
    program_id?: number;
    include_archived?: boolean;
  }): Promise<CaseEpisode[]> {
    const res = await this.client.get(`/api/case-episodes/household/${householdId}`, { params });
    return res.data;
  }

  async detectHouseholdNewCase(data: HouseholdNewCaseDetectRequest): Promise<NewCaseDetectResult> {
    const res = await this.client.post('/api/case-episodes/household/detect-new-case', data);
    return res.data;
  }
}

// Singleton export
export const caseEpisodesAPI = new CaseEpisodesAPI();
